/**
 * **卡牌的姿态参数** —— 摆在哪、怎么歪。
 *
 * 这是"假透视"的全部输入。刻意做成**一个扁平的纯数据对象**，理由：
 *
 * 1. 它是纯函数 `projectCorners()` 的输入 → 可以在 node 里单测，不用开浏览器
 * 2. 将来做补间动画时，插值就是对这个对象做 `lerpPose()`，不需要动画系统
 * 3. 联机/回放时它可以被序列化、被记录
 *
 * ⚠️ 这里**没有** `width` / `height` —— 形状属于通用层（`metrics.ts`），
 * 姿态只管"摆在哪、歪多少"。两者分开，牌面画法就永远改不了比例。
 */

/** 一张牌当前"摆成什么样"。字段全部必填（由 `resolvePose` 填默认值） */
export interface CardPose {
  /** 卡牌**中心**在逻辑画布上的位置 */
  readonly x: number;
  readonly y: number;

  /** 统一缩放。1 = 按 `metrics` 的尺寸。悬停放大之类改它 */
  readonly scale: number;

  /**
   * 绕 **Y 轴**转（左右转），弧度。**正值 = 右边向后倒**。
   *
   * 视觉上就是水平方向的梯形：近的那条竖边更长，远的那条更短。
   */
  readonly yaw: number;

  /**
   * 绕 **X 轴**转（上下翻），弧度。**正值 = 上边向后倒**。
   *
   * 这是最像 Balatro 的那个方向：牌像立着往后靠了一点。
   */
  readonly pitch: number;

  /**
   * 绕 **Z 轴**转（平面内旋转），弧度。正值 = 顺时针。
   *
   * ⚠️ 它是在**投影之前**应用的，也就是"牌绕自己的法线转"，
   * 而不是"把投影结果转一下"。倾斜方向会跟着一起转，这才是对的。
   */
  readonly roll: number;

  /**
   * **相机距离**，单位是「卡牌长边」。
   *
   * - 越大 → 越接近正交投影（越平）
   * - 越小 → 近大远小越夸张
   *
   * 默认 4，那是很收敛的一点点透视。想夸张到"从侧面看一张牌"就往 1.5 压。
   * ⚠️ 下限是 `MIN_PERSPECTIVE`：再近就会有角点跑到相机背后，投影会翻面。
   */
  readonly perspective: number;
}

/** 允许局部指定；没给的字段用默认值 */
export type PoseInput = Partial<CardPose>;

/**
 * `perspective` 的下限。
 *
 * 相机距离必须大于卡牌在视线方向上的半跨度，否则角点会跑到相机背后。
 * 2:3 的牌、yaw/pitch 各 1 弧度时，最大跨度约 `0.83 × 长边`，
 * 1.5 留了足够余量。（`projection.ts` 里还有一道逐角点的硬保护。）
 */
export const MIN_PERSPECTIVE = 1.5;

/** 平放、不歪、正对镜头 —— 也就是"什么效果都没有"的那一档 */
export const DEFAULT_POSE: CardPose = {
  x: 0,
  y: 0,
  scale: 1,
  yaw: 0,
  pitch: 0,
  roll: 0,
  perspective: 4,
};

/** 把局部参数补成完整姿态 */
export function resolvePose(input: PoseInput = {}): CardPose {
  return {
    x: input.x ?? DEFAULT_POSE.x,
    y: input.y ?? DEFAULT_POSE.y,
    scale: input.scale ?? DEFAULT_POSE.scale,
    yaw: input.yaw ?? DEFAULT_POSE.yaw,
    pitch: input.pitch ?? DEFAULT_POSE.pitch,
    roll: input.roll ?? DEFAULT_POSE.roll,
    perspective: Math.max(input.perspective ?? DEFAULT_POSE.perspective, MIN_PERSPECTIVE),
  };
}

/**
 * 两个姿态之间插值。
 *
 * 刻意**不做角度最短路径**（不处理 ±π 绕圈）：
 * 卡牌的倾斜量永远在小角度区间（±1 弧度以内），
 * 引入最短路径只会让"从 -0.2 慢慢转到 +0.2"这种最常见的用例变得难以预测。
 */
export function lerpPose(from: CardPose, to: CardPose, t: number): CardPose {
  const mix = (a: number, b: number): number => a + (b - a) * t;
  return {
    x: mix(from.x, to.x),
    y: mix(from.y, to.y),
    scale: mix(from.scale, to.scale),
    yaw: mix(from.yaw, to.yaw),
    pitch: mix(from.pitch, to.pitch),
    roll: mix(from.roll, to.roll),
    perspective: mix(from.perspective, to.perspective),
  };
}
